import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Check, Shield } from 'lucide-react';
import { Input, Button, Label, Badge } from '../ui';
import { getAllEquipos } from '../../lib/supabase';
import { cn } from '../../lib/utils';

const EquiposSelector = ({
  value,
  onChange,
  onCreateNew,
  placeholder = "Seleccionar equipo rival",
  className = ""
}) => {
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedEquipo, setSelectedEquipo] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createShortName, setCreateShortName] = useState('');
  const [creating, setCreating] = useState(false);
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);

  const normalizeNombre = (v = '') => String(v).trim().replace(/\s+/g, ' ').toLowerCase();

  useEffect(() => { loadEquipos(); }, []);

  useEffect(() => {
    if (value && equipos.length > 0) {
      setSelectedEquipo(equipos.find((e) => e.id === value) || null);
    } else {
      setSelectedEquipo(null);
    }
  }, [value, equipos]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCreateForm(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadEquipos = async () => {
    try {
      setLoading(true);
      const { data } = await getAllEquipos();
      setEquipos(data || []);
    } catch { } finally { setLoading(false); }
  };

  const handleSelect = (equipo) => {
    setSelectedEquipo(equipo);
    onChange(equipo.id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleOpenCreate = () => {
    setCreateName(searchTerm.trim());
    setCreateShortName('');
    setShowCreateForm(true);
  };

  const handleConfirmCreate = async () => {
    if (!createName.trim() || !onCreateNew) return;
    setCreating(true);
    try {
      const created = await onCreateNew(createName.trim(), createShortName.trim());
      if (created?.id) {
        setEquipos((prev) => {
          if (prev.some((e) => e.id === created.id)) return prev;
          return [...prev, created].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
        });
        setSelectedEquipo(created);
        onChange(created.id);
      }
      setIsOpen(false);
      setShowCreateForm(false);
      setSearchTerm('');
    } catch { } finally { setCreating(false); }
  };

  const filteredEquipos = equipos.filter((eq) => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return eq.nombre?.toLowerCase().includes(t) || eq.nombre_corto?.toLowerCase().includes(t) || eq.ciudad?.toLowerCase().includes(t);
  });

  const exactMatch = searchTerm.trim().length >= 2 && equipos.some(
    (eq) => normalizeNombre(eq.nombre) === normalizeNombre(searchTerm)
  );

  return (
    <div ref={wrapperRef} className={cn("relative", className)}>
      {/* Trigger */}
      <Button
        type="button"
        variant="outline"
        className="w-full justify-between font-normal h-10"
        onClick={() => { setIsOpen(!isOpen); setShowCreateForm(false); }}
      >
        <span className="flex items-center gap-2 truncate">
          {selectedEquipo ? (
            <>
              {selectedEquipo.logo_url ? (
                <img src={selectedEquipo.logo_url} alt="" className="h-5 w-5 object-contain rounded" />
              ) : (
                <Shield className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="font-medium">{selectedEquipo.nombre}</span>
              {selectedEquipo.ciudad && <span className="text-xs text-muted-foreground">({selectedEquipo.ciudad})</span>}
            </>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
        </span>
        <Search className="h-4 w-4 text-muted-foreground ml-2 shrink-0" />
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-background border rounded-lg shadow-lg max-h-72 overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                ref={searchRef}
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setShowCreateForm(false); }}
                placeholder="Buscar equipo..."
                className="pl-8 h-8 text-sm"
                autoFocus
              />
            </div>
          </div>

          {showCreateForm ? (
            /* Mini-form de crear equipo */
            <div className="p-3 space-y-3">
              <p className="text-xs text-muted-foreground">Crear nuevo equipo rival</p>
              <div className="space-y-2">
                <Label className="text-xs">Nombre completo *</Label>
                <Input value={createName} onChange={(e) => setCreateName(e.target.value)} placeholder="Ej: Águilas del Norte" className="h-8 text-sm" autoFocus />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Nombre corto</Label>
                <Input value={createShortName} onChange={(e) => setCreateShortName(e.target.value)} placeholder="Ej: Águilas" className="h-8 text-sm" />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" className="flex-1 h-8" onClick={() => setShowCreateForm(false)} disabled={creating}>
                  Cancelar
                </Button>
                <Button type="button" size="sm" className="flex-1 h-8" onClick={handleConfirmCreate} disabled={creating || !createName.trim()}>
                  {creating ? 'Creando...' : 'Crear equipo'}
                </Button>
              </div>
            </div>
          ) : (
            /* Lista de equipos */
            <>
              <div className="max-h-48 overflow-y-auto">
                {loading ? (
                  <p className="p-3 text-center text-sm text-muted-foreground">Cargando equipos...</p>
                ) : filteredEquipos.length === 0 ? (
                  <p className="p-3 text-center text-sm text-muted-foreground">No se encontraron equipos</p>
                ) : (
                  filteredEquipos.map((equipo) => (
                    <button
                      key={equipo.id}
                      type="button"
                      onClick={() => handleSelect(equipo)}
                      className={cn(
                        "w-full flex items-center gap-2 px-3 py-2 hover:bg-muted text-left text-sm transition-colors",
                        selectedEquipo?.id === equipo.id && "bg-primary/5"
                      )}
                    >
                      {equipo.logo_url ? (
                        <img src={equipo.logo_url} alt="" className="h-6 w-6 object-contain rounded shrink-0" />
                      ) : (
                        <div className="h-6 w-6 rounded bg-muted flex items-center justify-center shrink-0">
                          <Shield className="h-3 w-3 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{equipo.nombre}</p>
                        {(equipo.nombre_corto || equipo.ciudad) && (
                          <p className="text-xs text-muted-foreground truncate">
                            {[equipo.nombre_corto, equipo.ciudad].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>
                      {selectedEquipo?.id === equipo.id && <Check className="h-4 w-4 text-primary shrink-0" />}
                    </button>
                  ))
                )}
              </div>

              {/* Crear nuevo */}
              {onCreateNew && searchTerm.trim().length >= 2 && !exactMatch && (
                <div className="border-t">
                  <button
                    type="button"
                    onClick={handleOpenCreate}
                    className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-muted text-left text-sm text-primary transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Crear equipo "<strong>{searchTerm.trim()}</strong>"
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default EquiposSelector;
